import { Box, Typography } from "@mui/material";
import { SxProps } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { CustomButton } from "src/design";
import { colorTextPrimary } from "src/design/tokens";
import { TableScheduleDateNav } from "./TableScheduleDateNav";
import { TableCountControl } from "./TableCountControl";

interface ScheduleHeaderProps {
  date: Dayjs;
  onDateChange: (date: Dayjs) => void;
  numberOfTables: number;
  canUpdateTables: boolean;
  isUpdatingTables: boolean;
  onTableCountChange: (numberOfTables: number) => void;
  canCreate: boolean;
  isPlacing: boolean;
  onCreateClick: () => void;
  onCancelPlacement: () => void;
  sx?: SxProps;
}

/**
 * Title, day navigation, table count and the create action. Counters live in the
 * grid itself and the colour key in the legend, so this band stays thin — every
 * pixel it gives back is a pixel of board, which matters most on a phone where
 * the chrome is what pushes the day off screen.
 *
 * The four controls sit in one flat wrapping row rather than nested groups: a
 * nested group wraps as a whole, which on a narrow screen put each control on a
 * line of its own. Flat, they pack two to a line, and `ml: auto` still pins the
 * create button to the right of whichever line it lands on.
 */
export const ScheduleHeader = ({
  date,
  onDateChange,
  numberOfTables,
  canUpdateTables,
  isUpdatingTables,
  onTableCountChange,
  canCreate,
  isPlacing,
  onCreateClick,
  onCancelPlacement,
  sx,
}: ScheduleHeaderProps) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: { xs: 0.75, sm: 1.5 },
        flexShrink: 0,
        ...sx,
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: { xs: 15, sm: 18, md: 20 },
          color: colorTextPrimary,
          lineHeight: 1.2,
        }}
      >
        {t("tableSchedule.pageTitle")}
      </Typography>

      <TableScheduleDateNav date={date} onChange={onDateChange} />

      <TableCountControl
        numberOfTables={numberOfTables}
        canUpdate={canUpdateTables}
        isPending={isUpdatingTables}
        onChange={onTableCountChange}
      />

      {canCreate && (
        <CustomButton
          color="black"
          variant="contained"
          size="small"
          startIcon={isPlacing ? <CloseIcon /> : <AddIcon />}
          onClick={isPlacing ? onCancelPlacement : onCreateClick}
          sx={{ whiteSpace: "nowrap", py: { xs: 0.4, sm: 0.75 }, ml: "auto" }}
        >
          {isPlacing
            ? t("tableSchedule.placement.cancel")
            : t("bookings.createButton")}
        </CustomButton>
      )}
    </Box>
  );
};
